import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styles from './PurposeDrawerBody.styles';
import TabLayout from '../../components/shared/TabLayout';
import { useIntl } from 'react-intl';
import { ProcessingPurposeData } from '../../@types';
import PurposeInfo from './PurposeInfoTab/PurposeInfo';
import { selectDisplayPrivacyPolicy } from '../../redux/store/appData/privacyPolicy';
import { GroupedPurposeView } from './types';
import { getPurposeBaseName, getUsersPurpose } from '../../utils';

type Props = {
  selectedPurpose: ProcessingPurposeData;
};

const PurposeDrawerBody = ({ selectedPurpose }: Props) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(0);
  const displayPolicies = useSelector(selectDisplayPrivacyPolicy);

  // Find the policy containing this purpose from display list
  const parentPolicy = useMemo(() => {
    return displayPolicies?.find((policy) =>
      policy?.processingPurposes?.some((p) => p.id === selectedPurpose?.id)
    );
  }, [displayPolicies, selectedPurpose?.id]);

  // Compute grouped view from selected purpose
  const groupedPurpose = useMemo((): GroupedPurposeView => {
    const baseName = getPurposeBaseName(selectedPurpose?.name || '');
    const allPurposes = parentPolicy?.processingPurposes || [];

    // Find all purposes with the same base name and activation event
    const relatedPurposes = allPurposes.filter(
      (p) =>
        getPurposeBaseName(p.name || '') === baseName &&
        p.activationEventKey === selectedPurpose?.activationEventKey
    );

    // If no related purposes found, just use the selected one
    const purposes =
      relatedPurposes.length > 0 ? relatedPurposes : [selectedPurpose];

    // Merge collection names and field groups from all purposes
    const collectionNames = [
      ...new Set(
        purposes
          .map((p) => p.collectionName)
          .filter((c): c is string => !!c && c.length > 0)
      ),
    ];
    const fieldGroupKeys = [
      ...new Set(
        purposes.flatMap((p) => p.fieldGroupKeys || []).filter((g) => !!g)
      ),
    ];

    // Fallback: if no collections found, use selected purpose's collection
    if (collectionNames.length === 0 && selectedPurpose?.collectionName) {
      collectionNames.push(selectedPurpose.collectionName);
    }

    // Fallback: if no field groups found, use selected purpose's field groups
    if (
      fieldGroupKeys.length === 0 &&
      selectedPurpose?.fieldGroupKeys?.length > 0
    ) {
      fieldGroupKeys.push(...selectedPurpose.fieldGroupKeys);
    }

    return {
      baseName,
      activationEventKey: selectedPurpose?.activationEventKey || '',
      minDuration: selectedPurpose?.minDuration || '',
      maxDuration: selectedPurpose?.maxDuration || '',
      legalBasis: selectedPurpose?.legalBasis || '',
      // Overrides live only on the users sibling, so resolve them from there
      // rather than from whichever collection's row was clicked.
      retentionOverrides: getUsersPurpose(purposes)?.retentionOverrides || [],
      collectionNames,
      fieldGroupKeys,
      purposes,
      status: selectedPurpose?.status || '',
      policyName: selectedPurpose?.policyName || '',
    };
  }, [selectedPurpose, parentPolicy]);

  const tabs = [
    {
      title: (
        <span>
          {intl.formatMessage({
            id: `fleetadmin.users.tab.info`,
          })}
        </span>
      ),
      content: <PurposeInfo groupedPurpose={groupedPurpose} />,
    },
  ];

  return (
    <>
      <div className="purpose-drawer-body">
        <div className="purpose-name-wrapper">
          <p className="name">{groupedPurpose.baseName}</p>
        </div>
        <TabLayout
          tabs={tabs}
          keyName="purpose-drawer"
          activeTab={activeTab}
          onChangeTab={(activeKey) => {
            setActiveTab(parseInt(activeKey.split('-').pop() || '0', 10));
          }}
        />
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default PurposeDrawerBody;
